import PointerInteraction, { Options } from 'ol/interaction/Pointer';
import { MapBrowserEvent, Feature, Map } from 'ol';
import VectorSource from 'ol/source/Vector';
import LineString from 'ol/geom/LineString';
import TrackLineFeature from './TrackLineFeature';
import AnchorPointFeature from './AnchorPointFeature';
import { Coordinate, equals } from 'ol/coordinate';
import CustomInteractionLifecycle from './CustomInteractionLifecycle';
import { RouteDelegate } from '@/RouteDelegate';
import Point from 'ol/geom/Point';
import { Pixel } from 'ol/pixel';
import { GeometryHelpers } from '@/geometryhelpers';
import BaseEvent from 'ol/events/Event';
import PreUndoEvent from './PreUndoEvent';
import { Renderer } from './Renderer';
import * as _ from 'lodash'
import { Styles } from './Styles';
import SplitHelper from './SplitHelper';
import TrackVectorLayer from './TrackVectorLayer';
import { TrackVectorLayerType } from './TrackVectorLayerType';
import UserEventInfo from './UserEventInfo';
import JoinHelper from './JoinHelper';
import PointFeature from './PointFeature';
import EventRememberingPointerInteraction from './EventRememberingPointerInteraction';

const changeIntentToAtcfTimeout = 2000
const routingIntentPropertyKey = 'RoutingIntent'
const routingIntentAtcf = 'ATCF'
const routingIntentStandard = 'Standard'
export const nearby = 6 // pixels "nearby" tolerance when adding or removing Anchors

interface RerouteOptions {
    routeDelegate: RouteDelegate;
    reroutingLineFeatureSource: VectorSource<LineString>;
    joinIndicatorFeatureSource: VectorSource<Point>;
    renderer: Renderer<any>;
}

export enum RerouteEvents {
    routeerror = 'routeerror',
    warning = 'warning'
}

export class RerouteWarningEvent extends BaseEvent {
    message: string
    constructor(message: string) {
        super(RerouteEvents.warning)
        this.message = message
    }
}

export class RerouteErrorEvent extends BaseEvent {
    message: string
    constructor(message: string) {
        super(RerouteEvents.routeerror)
        this.message = message
    }
}

interface HoldInfo {
    func: _.DebouncedFunc<() => void>;
    pixel: Pixel;
}

export interface RerouteConstructor<T> {
    new(options: RerouteOptions, superOptions?: Options): T;
}

export abstract class RerouteBase extends EventRememberingPointerInteraction implements CustomInteractionLifecycle {
    aNumber = 1
    isRerouting = false
    reroutingStartPixel: Pixel | null = null
    reroutingObject: TrackLineFeature | AnchorPointFeature | PointFeature| null = null
    rerouteStartAnchor: Coordinate | null = null
    rerouteEndAnchor: Coordinate | null = null
    reroutingLine: Feature<LineString> | null = null
    implementsCustomInteractionLifecycle = true
    options: RerouteOptions
    holdInfo: HoldInfo|null = null
    joinHelper: JoinHelper

    constructor(options: RerouteOptions, superOptions?: Options) {
        super(superOptions)
        this.options = options
        this.joinHelper = new JoinHelper(options.joinIndicatorFeatureSource, this, options.renderer)
    }

    finish() {
        this.removeReroutingLineIfExists()
    }

    removeReroutingLineIfExists() {
        if (this.reroutingLine !== null && this.options.reroutingLineFeatureSource.hasFeature(this.reroutingLine)) {
            this.options.reroutingLineFeatureSource.removeFeature(this.reroutingLine)
            this.reroutingLine = null
        }
    }

    handleDownEvent(event: MapBrowserEvent<any>) {
        const features = event.map.getFeaturesAtPixel(event.pixel)
        this.reroutingStartPixel = event.pixel
        const featureset = Array.from(new Set(features)) // Set is necessary as it sometimes returns the same one twice
        const points = featureset.filter(i => i instanceof AnchorPointFeature) as AnchorPointFeature[]
        if (points.length > 0) {
            const pointChosen = _.last(_.sortBy(points, p => p.track.zIndex))!
            this.reroutingObject = pointChosen
        } else {
            const lines = featureset.filter(i => i instanceof TrackLineFeature) as TrackLineFeature[]
            if (lines.length >= 1) {
                this.reroutingObject = _.last(_.sortBy(lines, l => l.track.zIndex))!
            }
        }

        return this.reroutingObject !== null
    }

    setAnchors() {
        if (this.reroutingObject instanceof TrackLineFeature) {
            this.rerouteStartAnchor = this.reroutingObject.getGeometry().getFirstCoordinate()
            this.rerouteEndAnchor = this.reroutingObject.getGeometry().getLastCoordinate()
        } else if (this.reroutingObject instanceof AnchorPointFeature) {
            const allDots = this.reroutingObject.track.getDots();
            const pointIndex = allDots.indexOf(this.reroutingObject)
            if (pointIndex < 0) {
                throw new Error("Point to reroute not in track's dot feature source")
            }
            if (allDots.length < 2) {
                throw new Error("Trying to reroute a point of a track that only has one dot")
            }

            if (pointIndex === 0) {
                // rerouting the start point of the track
                this.rerouteStartAnchor = null
                this.rerouteEndAnchor = allDots[1].getGeometry().getCoordinates()
            } else if (pointIndex == allDots.length - 1) {
                // rerouting the end point of the track
                this.rerouteStartAnchor = allDots[allDots.length - 2].getGeometry().getCoordinates()
                this.rerouteEndAnchor = null
            } else {
                this.rerouteStartAnchor = allDots[pointIndex - 1].getGeometry().getCoordinates()
                this.rerouteEndAnchor = allDots[pointIndex + 1].getGeometry().getCoordinates()
            }
        } else {
            console.warn('reroutingObject is of unknown type ', this.reroutingObject)
        }
    }

    startRerouting(event: MapBrowserEvent<any>) {
        this.isRerouting = true

        this.removeReroutingLineIfExists()
        this.setAnchors()
        this.reroutingLine = new Feature<LineString>(this.getRerouteLineGeometry(event))
        this.options.reroutingLineFeatureSource.addFeature(this.reroutingLine)
        
        this.resetAtcfIntent(event.pixel)
    }

    changeIntentToAtcf() {
        if(this.reroutingLine) {
            RerouteBase.setAtcfIntentOnReroutingLine(this.reroutingLine)
        }
    }

    resetAtcfIntent(pixel: Pixel) {
        if(this.holdInfo) {
            this.holdInfo.func.cancel()
        }
        this.holdInfo =  {
            func: _.debounce(this.changeIntentToAtcf.bind(this), changeIntentToAtcfTimeout),
            pixel: pixel
        }
        this.holdInfo.func()
    }

    stopRerouting() {
        this.removeReroutingLineIfExists()
        this.reroutingStartPixel = null
        this.reroutingLine = null
        this.reroutingObject = null
        this.isRerouting = false
    }

    handleDragEvent(event: MapBrowserEvent<any>) {
        if (!this.isRerouting && this.reroutingObject !== null && this.reroutingStartPixel !== null && !GeometryHelpers.isVeryClose(this.reroutingStartPixel, event.pixel)) {
            // do we need to start re-routing? (not already, but satisfy the conditions to be)
            this.startRerouting(event)
        }

        //basically means 'move event when the mouse is down'
        if (this.reroutingLine !== null) {
            this.reroutingLine.setGeometry(this.getRerouteLineGeometry(event))

            if(this.holdInfo && !GeometryHelpers.isVeryClose(event.pixel, this.holdInfo.pixel)) {
                RerouteBase.unsetAtcfIntentOnReroutingLine(this.reroutingLine)
                this.resetAtcfIntent(event.pixel)
            }

            if(this.reroutingObject !== null && this.reroutingObject instanceof AnchorPointFeature) {
                this.checkFeatureToJoin(event, this.reroutingObject)
            }

            return true
        }
        return super.handleMoveEvent(event)
    }

    doCheckFeatureToJoin(event: MapBrowserEvent<any>, reroutingTrackpointFeature: AnchorPointFeature) {
        const featuresNearby = event.map.getFeaturesAtPixel(event.pixel, {
            hitTolerance: 10,
            layerFilter: layerSource => layerSource instanceof TrackVectorLayer && layerSource.type === TrackVectorLayerType.Dots
        })
        const joinableTrackpointFeaturesNearby = featuresNearby
            .filter(f => f instanceof AnchorPointFeature && RerouteBase.canJoin(f, reroutingTrackpointFeature))
            .map(f => f as AnchorPointFeature)
        const trackpointFeatureToJoin = _.last(_.sortBy(joinableTrackpointFeaturesNearby, f => f.track.zIndex))
        if(trackpointFeatureToJoin) {
            this.joinHelper.setFeatureToJoin(trackpointFeatureToJoin)
        } else {
            this.joinHelper.unsetFeatureToJoin()
        }
    }
    checkFeatureToJoin = _.throttle(this.doCheckFeatureToJoin, 200)

    static canJoin(tp1: AnchorPointFeature, tp2: AnchorPointFeature) {
        const retval = tp1.track.value !== tp2.track.value && 
            ((tp1.isFirst && tp2.isLast) || (tp2.isFirst && tp1.isLast))
        return retval
    }

    static setAtcfIntentOnReroutingLine(routingLine: Feature<LineString>) {
        routingLine.setStyle(Styles.ThickTransparentLightGreen)
        routingLine.set(routingIntentPropertyKey, routingIntentAtcf)
    }

    static unsetAtcfIntentOnReroutingLine(routingLine: Feature<LineString>) {
        routingLine.setStyle(Styles.ThickTransparentLightBlue)
        routingLine.set(routingIntentPropertyKey, routingIntentStandard)
    }

    handleUpEventWhenRerouting(event: MapBrowserEvent<any>): boolean {
        if (this.reroutingObject instanceof AnchorPointFeature) {
            if (this.reroutingObject.isFirst) {
                return this.rerouteStartPoint(event, this.reroutingObject)
            } else if (this.reroutingObject.isLast) {
                return this.rerouteEndPoint(event, this.reroutingObject)
            } else {
                return this.rerouteMidPoint(event, this.reroutingObject)
            }
        } else if (this.reroutingObject instanceof TrackLineFeature) {
            return this.rerouteLine(event, this.reroutingObject)
        }
        return super.handleUpEvent(event)
        
    }
    handleUpEvent(event: MapBrowserEvent<any>): boolean {
        if (this.isRerouting) {
            return this.handleUpEventWhenRerouting(event)
        } else {
            // prevent a full click then an entirely separate drag of the map rerouting the initial object.
            // should return to initial state
            this.reroutingObject = null
            this.reroutingStartPixel = null
        }
        return super.handleUpEvent(event)
    }

    intendsAtcf() {
        return !!this.reroutingLine && this.reroutingLine.get(routingIntentPropertyKey) === routingIntentAtcf
    }

    rerouteStartPoint(event: MapBrowserEvent<any>, trackPointFeature: AnchorPointFeature) {
        if (this.rerouteEndAnchor === null) {
            throw new Error("reroute.rerouteStartPoint: rerouteEndAnchor is null")
        }
        const allTrackSegments = trackPointFeature.track.getSegments()
        
        const joinInfo = this.joinHelper.getJoinInfo(event)
        const request = new LineString([joinInfo.coord, this.rerouteEndAnchor])

        this.options.routeDelegate.doRoute(request, response => {
                this.dispatchEvent(new PreUndoEvent([trackPointFeature.track.getRestoreData(this.options.renderer)], 'move start point'))
                const endEvent = undefined // indicates to the snap alg that end is fixed
                const snappedLineInfo = this.options.routeDelegate.getSnappedRouteInfo(request, response, joinInfo.event, endEvent, this.intendsAtcf())

                allTrackSegments[0].setGeometry(snappedLineInfo.trackLineToUse)
                trackPointFeature.setGeometry(new Point(snappedLineInfo.startPointToUse))
                this.stopRerouting()

                if(joinInfo.trackToJoin) {
                    this.joinHelper.joinTracks(joinInfo.trackToJoin, trackPointFeature.track)
                }
            },
            (errorMessage) => {
                this.dispatchEvent(new RerouteErrorEvent(errorMessage))
                this.stopRerouting()
            })
        return true
    }

    rerouteMidPoint(event: MapBrowserEvent<any>, trackPointFeature: AnchorPointFeature) {
        const allDots = trackPointFeature.track.getDots()
        const allTracks = trackPointFeature.track.getSegments()

        const thisDotIndex = allDots.indexOf(trackPointFeature)
        const start = allDots[thisDotIndex - 1]
        const end = allDots[thisDotIndex + 1]

        const segmentA = new LineString([start.getGeometry().getCoordinates(), event.coordinate])
        const segmentB = new LineString([event.coordinate, end.getGeometry().getCoordinates()])
        if (thisDotIndex >= allTracks.length) {
            throw new Error("rerouteMidPoint: Invalid dot index")
        }

        this.options.routeDelegate.doRouteMulti([segmentA, segmentB],
            results => {
                this.dispatchEvent(new PreUndoEvent([trackPointFeature.track.getRestoreData(this.options.renderer)], 're-route'))

                // the 'mid point' can only be snapped once (result should be the same regardless) so let the first one do it.
                const snappedLineInfoFormer = this.options.routeDelegate.getSnappedRouteInfo(segmentA, results[0], undefined, event, this.intendsAtcf()) //fixed start, floating end
                const requestB = new LineString([snappedLineInfoFormer.endPointToUse, segmentB.getLastCoordinate()])
                const snappedLineInfoLatter = this.options.routeDelegate.getSnappedRouteInfo(requestB, results[1], undefined, undefined, this.intendsAtcf()) //both are fixed. start would have been floating, but the first call has snapped it

                // sanity check:
                if(!equals(snappedLineInfoFormer.endPointToUse, snappedLineInfoLatter.startPointToUse)) {
                    console.error('rerouteMidPoint: snappedLineInfoFormer.endPointToUse should match snappedLineInfoLatter.startPointToUse: ', 
                    snappedLineInfoFormer.endPointToUse, 
                    snappedLineInfoLatter.startPointToUse)
                }
                if(!equals(snappedLineInfoFormer.trackLineToUse.getLastCoordinate(), snappedLineInfoLatter.trackLineToUse.getFirstCoordinate())) {
                    console.error('rerouteMidPoint: snappedLineInfoFormer.trackLineToUse.getLastCoordinate should match snappedLineInfoLatter.trackLineToUse.getFirstCoordinate', 
                    snappedLineInfoFormer.trackLineToUse.getLastCoordinate(), 
                    snappedLineInfoLatter.trackLineToUse.getFirstCoordinate())
                }

                allTracks[thisDotIndex - 1].setGeometry(snappedLineInfoFormer.trackLineToUse)
                allTracks[thisDotIndex].setGeometry(snappedLineInfoLatter.trackLineToUse)

                trackPointFeature.setGeometry(new Point(snappedLineInfoFormer.endPointToUse))
                this.stopRerouting()
            }, (errorMessage) => {
                this.dispatchEvent(new RerouteErrorEvent(errorMessage))
                this.stopRerouting()
            })
        return true
    }

    static userEventInfoFromCoordinate(map: Map, coord: Coordinate): UserEventInfo {
        return {
            coordinate: coord,
            pixel: map.getPixelFromCoordinate(coord)
        }
    }

    rerouteEndPoint(event: MapBrowserEvent<any>, trackPointFeature: AnchorPointFeature) {
        if (this.rerouteStartAnchor === null) {
            throw new Error("reroute.rerouteStartPoint: rerouteEndAnchor is null")
        }
        const allTrackSegments = trackPointFeature.track.getSegments()
        const joinInfo = this.joinHelper.getJoinInfo(event)
        const request = new LineString([this.rerouteStartAnchor, joinInfo.coord])

        this.options.routeDelegate.doRoute(request, response => {
                this.dispatchEvent(new PreUndoEvent([trackPointFeature.track.getRestoreData(this.options.renderer)], 'move end point'))
                const snappedLineInfo = this.options.routeDelegate.getSnappedRouteInfo(request, response, undefined, joinInfo.event, this.intendsAtcf()) // start is fixed
                allTrackSegments[allTrackSegments.length - 1].setGeometry(snappedLineInfo.trackLineToUse)
                trackPointFeature.setGeometry(new Point(snappedLineInfo.endPointToUse))
                this.stopRerouting()

                if(joinInfo.trackToJoin) {
                    this.joinHelper.joinTracks(trackPointFeature.track, joinInfo.trackToJoin)
                }
            },
            (errorMessage) => {
                this.dispatchEvent(new RerouteErrorEvent(errorMessage))
                this.stopRerouting()
            })
        return true
    }

    rerouteLine(event: MapBrowserEvent<any>, trackLineFeature: TrackLineFeature) {
        const allTracks = trackLineFeature.track.getSegments()
        const thisTrackIndex = allTracks.indexOf(trackLineFeature)
        // if it's the last one, then it's the order + 1. Otherwise, it's the average of this one and the next one.

        const start = trackLineFeature.getGeometry().getFirstCoordinate()
        const end = trackLineFeature.getGeometry().getLastCoordinate()
        const segmentA = new LineString([start, event.coordinate])
        const segmentB = new LineString([event.coordinate, end])
        this.options.routeDelegate.doRouteMulti([segmentA, segmentB],
            results => {
                // the 'mid point' can only be snapped once (result should be the same regardless) so let the first one do it.
                const snappedLineInfoFormer = this.options.routeDelegate.getSnappedRouteInfo(segmentA, results[0], undefined, event, this.intendsAtcf()) //fixed start, floating end
                const requestB = new LineString([snappedLineInfoFormer.endPointToUse, segmentB.getLastCoordinate()])
                const snappedLineInfoLatter = this.options.routeDelegate.getSnappedRouteInfo(requestB, results[1], undefined, undefined, this.intendsAtcf()) //both are fixed. start would have been floating, but the first call has snapped it                    

                // sanity check:
                if(!equals(snappedLineInfoFormer.endPointToUse, snappedLineInfoLatter.startPointToUse)) {
                    console.error('rerouteLine: snappedLineInfoFormer.endPointToUse should match snappedLineInfoLatter.startPointToUse: ', 
                    snappedLineInfoFormer.endPointToUse, 
                    snappedLineInfoLatter.startPointToUse)
                }
                if(!equals(snappedLineInfoFormer.trackLineToUse.getLastCoordinate(), snappedLineInfoLatter.trackLineToUse.getFirstCoordinate())) {
                    console.error('rerouteLine: snappedLineInfoFormer.trackLineToUse.getLastCoordinate should match snappedLineInfoLatter.trackLineToUse.getFirstCoordinate', 
                    snappedLineInfoFormer.trackLineToUse.getLastCoordinate(), 
                    snappedLineInfoLatter.trackLineToUse.getFirstCoordinate())
                }
                
                this.dispatchEvent(new PreUndoEvent([trackLineFeature.track.getRestoreData(this.options.renderer)], 're-route'))

                trackLineFeature.setGeometry(snappedLineInfoFormer.trackLineToUse)
                trackLineFeature.track.addSegment(snappedLineInfoLatter.trackLineToUse, thisTrackIndex + 1)
                trackLineFeature.track.addDot(new Point(snappedLineInfoFormer.endPointToUse), thisTrackIndex + 1)
                this.stopRerouting()
            }, (errorMessage) => {
                this.dispatchEvent(new RerouteErrorEvent(errorMessage))
                this.stopRerouting()
            })
        return true
    }

    handleEvent(event: MapBrowserEvent<any>) {
        if (event.type === 'dblclick') {
            return this.handleDblClick(event)
        } else if (event.type === 'singleclick') {
            return this.handleSingleClick(event)
        }
        return super.handleEvent(event);
    }

    handleSingleClick(event: MapBrowserEvent<any>) {
        const featuresAtPoint = event.map.getFeaturesAtPixel(event.pixel, { hitTolerance: nearby });
        const linesAtPoint = featuresAtPoint.filter(f => f instanceof TrackLineFeature) as TrackLineFeature[] 
        if(linesAtPoint.length > 0) {
            const dotsAtPoint = featuresAtPoint.filter(p => p instanceof AnchorPointFeature) as AnchorPointFeature[] 
            if (dotsAtPoint.length == 0) { // don't add an anchor if there is already one here
                return this.addAnchor(event, linesAtPoint[0])
            }
        }
        return super.handleEvent(event)
    }

    addAnchor(event: MapBrowserEvent<any>, trackLineFeature: TrackLineFeature) {
        const split = SplitHelper.splitLineAt(trackLineFeature.getGeometry(), event.coordinate)

        if(split.secondTrackGeometry.getCoordinates().length <= 1) {
            this.dispatchEvent(new RerouteWarningEvent('Cannot add an Anchor here as the nearest point is the end of the track. Try clicking nearer the middle of the track.'))
        }
        else if(split.firstTrackGeometry.getCoordinates().length <= 1) {
            this.dispatchEvent(new RerouteWarningEvent('Cannot add an Anchor here as the nearest point is the start of the track. Try clicking nearer the middle of the track.'))
        } else {
            this.dispatchEvent(new PreUndoEvent([trackLineFeature.track.getRestoreData(this.options.renderer)], 'add point'))
            trackLineFeature.setGeometry(split.firstTrackGeometry)
            const segmentIndex = trackLineFeature.track.getSegments().indexOf(trackLineFeature)
            trackLineFeature.track.addSegment(split.secondTrackGeometry, segmentIndex + 1)
            trackLineFeature.track.addDot(new Point(split.firstTrackGeometry.getLastCoordinate()), segmentIndex + 1)
        }
        return true
    }

    handleDblClick(event: MapBrowserEvent<any>) {
        const pointFeatures = event.map.getFeaturesAtPixel(event.pixel, {hitTolerance: nearby}).filter(p => p instanceof AnchorPointFeature) as AnchorPointFeature[] 
        const pointToRemove = _.last(_.sortBy(pointFeatures, p => p.track.zIndex))
        if (pointToRemove) { // _.last will return undefined if there aren't any
            this.removeAnchorAt(pointToRemove)
            return false
        }
        return super.handleEvent(event)
    }

    removeAnchorAt(point: AnchorPointFeature) {
        const allPoints = point.track.getDots()
        const thisPointIndex = allPoints.indexOf(point)
        const allTracks = point.track.getSegments();
        
        if (thisPointIndex > 0 && thisPointIndex < allPoints.length - 1) {
            // mid point - reroute
            const start = allPoints[thisPointIndex - 1].getGeometry().getCoordinates()
            const end = allPoints[thisPointIndex + 1].getGeometry().getCoordinates()
            const segmentToReroute = new LineString([start, end])
            const trackToKeep = allTracks[thisPointIndex - 1]
            const trackToRemove = allTracks[thisPointIndex]

            this.options.routeDelegate.doRoute(segmentToReroute,
                result => {
                    this.dispatchEvent(new PreUndoEvent([point.track.getRestoreData(this.options.renderer)], 'remove anchor'))
                    const snappedRouteInfo = this.options.routeDelegate.getSnappedRouteInfo(segmentToReroute, result, undefined, undefined, this.intendsAtcf())
                    trackToKeep.setGeometry(snappedRouteInfo.trackLineToUse)
                    point.track.removeSegment(trackToRemove)
                    point.track.removeDot(point)
                }, (errorMessage) => {
                    this.dispatchEvent(new RerouteErrorEvent(errorMessage))
                    this.stopRerouting()
                })

        } else {
            let trackToDelete = -1
            if (allTracks.length < 2) {
                this.dispatchEvent(new RerouteWarningEvent("Cannot delete a point if there are only two left"))
            } else if (thisPointIndex === allPoints.length - 1) {
                // last point
                trackToDelete = allTracks.length - 1
            } else if (thisPointIndex === 0) {
                trackToDelete = 0
            }
            if (trackToDelete >= 0) {
                this.dispatchEvent(new PreUndoEvent([point.track.getRestoreData(this.options.renderer)], 'remove anchor'))
                point.track.removeSegment(allTracks[trackToDelete])
                point.track.removeDot(point)
            }
        }
    }

    getRerouteLineGeometry(event: MapBrowserEvent<any>): LineString {
        if (this.rerouteStartAnchor === null && this.rerouteEndAnchor !== null) {
            return new LineString([event.coordinate, this.rerouteEndAnchor])
        } else if (this.rerouteStartAnchor !== null && this.rerouteEndAnchor === null) {
            return new LineString([this.rerouteStartAnchor, event.coordinate])
        } else if (this.rerouteStartAnchor !== null && this.rerouteEndAnchor !== null) {
            return new LineString([this.rerouteStartAnchor, event.coordinate, this.rerouteEndAnchor])
        } else {
            throw new Error("Both rerouteStartAnchor and rerouteEndAnchor are null")
        }
    }
}


export default class Reroute extends RerouteBase {
    
}